// The shared "materialise" canvas — a grid of cells, each coloured by a category id (a
// worker, or a job). The submitter's RunVisual uses it to show N workers tiling one job
// (cells = chunks, colour = workerId); the #contribute page uses it to show the chunks THIS
// tab has computed (cells = chunks done, colour = jobId). Same cheap canvas, two framings —
// "doubles as the mesh visual" (utility-redesign plan §4.2 / §8 Phase D).
//
// Pure DOM + canvas, no dependency. The categorical palette (styles.css --worker-*) is chosen
// to sit with the teal-and-greyscale brand; the brand teal #51c0a7 stays reserved for the
// accent/estimate role, not reused as a generic data colour (plan §6).

export const CATEGORY_PALETTE = [
  "#3a8f7c", // deep teal-green
  "#6c7bd1", // periwinkle
  "#d18a4e", // amber
  "#b85c8a", // mauve-pink
  "#5fa9d6", // sky
  "#8a9a4e", // olive
  "#c45d5d", // clay red
  "#7d6cb0", // violet
  "#2f7da0", // steel blue
  "#a8743a", // bronze
];
export const PENDING_CELL = "#eceeed"; // neutral grey for not-yet-computed cells
export const DONE_NEUTRAL = "#9fd9cc"; // soft teal tint for a landed cell with no known peer

/** Sentinel cell states (negative so they never collide with real category ids). */
export const CELL_PENDING = -1;
export const CELL_DONE_NEUTRAL = -2;

/**
 * Cell colour. >=0 → the categorical palette (a worker/job). CELL_DONE_NEUTRAL (-2) → a soft
 * teal "done" tint, used when a chunk has landed but we don't know which peer computed it.
 * Anything else negative → the pending grey.
 */
export function categoryColor(id: number): string {
  if (id === CELL_DONE_NEUTRAL) return DONE_NEUTRAL;
  if (id < 0) return PENDING_CELL;
  const n = CATEGORY_PALETTE.length;
  return CATEGORY_PALETTE[((id % n) + n) % n]!;
}

/**
 * Draw `cells` (each a category id; -1 = pending) into `canvas` as a square-ish grid laid out
 * in index order. `targetCols` biases the aspect ratio toward the canvas width. Cheap enough
 * to call per chunk even at high counts.
 */
export function drawCellGrid(canvas: HTMLCanvasElement, cells: ArrayLike<number>): void {
  const count = cells.length;
  if (count <= 0) return;
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 420;
  const cols = Math.max(1, Math.ceil(Math.sqrt(count * (cssW / 240))));
  const rows = Math.ceil(count / cols);
  const gap = count > 400 ? 1 : 2;
  const cell = Math.max(2, Math.floor((cssW - gap * (cols - 1)) / cols));
  const cssH = rows * cell + gap * (rows - 1);
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  canvas.style.height = cssH + "px";
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);
  for (let i = 0; i < count; i++) {
    const c = i % cols;
    const r = (i / cols) | 0;
    ctx.fillStyle = categoryColor(cells[i]!);
    ctx.fillRect(c * (cell + gap), r * (cell + gap), cell, cell);
  }
}
